import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { usePartnerAuth } from './PartnerAuthContext';

const PartnerBookingContext = createContext(null);

// Partner-scoped booking state (Option P). This is a completely SEPARATE flow
// from the customer BookingContext — it has its own state, its own hold
// countdown, and its own navigation helper. Nothing here imports from or
// mutates BookingContext, so the live customer flow is never touched. The
// partner identity is pulled from PartnerAuthContext (the approved partners
// row) so downstream booking screens can attribute the booking + apply the
// partner's TAC rate.
export function PartnerBookingProvider({ children }) {
  const navigate = useNavigate();
  const { partnerProfile } = usePartnerAuth();

  // Booking selections (mirrors the customer flow's selection state, isolated).
  const [selectedDate, setSelectedDate] = useState(null);        // ISO date (YYYY-MM-DD) from the partner date cards, or null
  const [passengers, setPassengers] = useState({ adults: 1, children: 0, infants: 0 });
  const [availabilityCruises, setAvailabilityCruises] = useState([]); // live availability result
  const [selectedCruise, setSelectedCruise] = useState(null);    // full cruise-card object
  const [paxDetails, setPaxDetails] = useState([]);              // per-passenger detail rows
  const [agreedTnC, setAgreedTnC] = useState(false);
  const [bookingRef, setBookingRef] = useState('');

  // The agent (sub-user of the partner agency) placing the booking. Wired up in
  // a later step (A2); kept here now so the shape is stable.
  const [agentId, setAgentId] = useState(null);

  // Booking hold — the live reservation placed by the `hold` edge function.
  // holdExpiresAt is the absolute expiry (ISO) returned by the backend; the
  // countdown below is derived from it so it stays correct across every screen.
  const [holdId, setHoldId] = useState(null);
  const [holdExpiresAt, setHoldExpiresAt] = useState(null);
  const [holdSeconds, setHoldSeconds] = useState(0);
  const [holdExpiredNotice, setHoldExpiredNotice] = useState(false);

  // Cross-screen fade — mirrors the customer flow's 250ms step transition.
  const [fading, setFading] = useState(false);

  // Partner identity, derived from the authenticated partner profile.
  const partnerId = partnerProfile?.id ?? null;
  const partnerName = partnerProfile?.name ?? null;
  const tacRate = partnerProfile?.tac_rate ?? null;

  // Drive the countdown from the absolute expiry. When it reaches zero the hold
  // is cleared and the partner is routed back to the partner availability
  // screen (date + party preserved). No release call is needed — the DB treats
  // an expired hold as free and the next create releases any prior hold.
  // NOTE: this DUPLICATES the BookingContext countdown pattern intentionally, so
  // the partner flow stays fully independent of the customer flow.
  useEffect(() => {
    if (!holdExpiresAt) {
      setHoldSeconds(0);
      return;
    }
    const expMs = new Date(holdExpiresAt).getTime();
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((expMs - Date.now()) / 1000));
      setHoldSeconds(remaining);
      return remaining;
    };
    const expire = () => {
      setHoldExpiredNotice(true);
      setHoldId(null);
      setHoldExpiresAt(null);
      setSelectedCruise(null);
      navigate('/partner/book/availability');
    };
    if (tick() <= 0) {
      expire();
      return;
    }
    const timer = setInterval(() => {
      if (tick() <= 0) {
        clearInterval(timer);
        expire();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [holdExpiresAt, navigate]);

  // Navigate with the original fade-out, applying the same state resets the
  // customer flow performs when entering certain steps (scoped to partner paths).
  const go = useCallback((path) => {
    setFading(true);
    setTimeout(() => {
      if (path === '/partner/book/date') setSelectedDate(null);
      // Party is NOT reset on navigation — it persists so the availability
      // screen's "Edit passengers" lets the partner adjust their current party
      // rather than starting over. Only the context's initial state seeds 1 adult.
      if (path === '/partner/book/availability') setSelectedCruise(null);
      navigate(path);
      setFading(false);
    }, 250);
  }, [navigate]);

  // Leave the booking flow entirely and release THIS partner's hold. Used by the
  // cruise summary's "Leave" confirm. Modeled on the customer BookingContext
  // exitFlow, but keyed on partnerId (not customerId). Resilient + non-blocking:
  // the release is fired and NOT awaited (the hold is partner-scoped server-side),
  // local hold state is cleared, and we navigate regardless of whether the
  // release call succeeds — the hold's own expiry is the real backstop.
  const exitFlow = useCallback(() => {
    try {
      if (partnerId) {
        supabase.functions
          .invoke('hold', { body: { action: 'release', partnerId } })
          .catch((err) => console.error('Partner hold release on flow exit failed (will expire on its own):', err));
      }
    } catch (err) {
      console.error('Partner hold release on flow exit failed (will expire on its own):', err);
    }
    setHoldId(null);
    setHoldExpiresAt(null);
    go('/partner/book/availability');
  }, [partnerId, go]);

  // Leave the booking flow entirely and return to the partner DASHBOARD. Used by
  // the "Exit to Dashboard" control on every booking step. Like exitFlow it
  // releases any active hold (fire-and-forget, non-fatal), but it goes further:
  // it resets ALL flow state so the next booking starts clean, and it navigates
  // to /partner (OUTSIDE the /partner/book subtree) via navigate() — not the
  // in-flow go() — so the provider unmounts cleanly with no fade/guard flicker.
  const exitToDashboard = useCallback(() => {
    try {
      if (holdId && partnerId) {
        supabase.functions
          .invoke('hold', { body: { action: 'release', partnerId } })
          .catch((err) => console.error('Partner hold release on dashboard exit failed (will expire on its own):', err));
      }
    } catch (err) {
      console.error('Partner hold release on dashboard exit failed (will expire on its own):', err);
    }
    // Clear the hold + reset every flow selection so a fresh booking is clean.
    setHoldId(null);
    setHoldExpiresAt(null);
    setHoldExpiredNotice(false);
    setSelectedDate(null);
    setPassengers({ adults: 1, children: 0, infants: 0 });
    setSelectedCruise(null);
    setPaxDetails([]);
    setAgreedTnC(false);
    setAgentId(null);
    setAvailabilityCruises([]);
    setBookingRef('');
    navigate('/partner');
  }, [holdId, partnerId, navigate]);

  const value = {
    // selections
    selectedDate, setSelectedDate,
    passengers, setPassengers,
    availabilityCruises, setAvailabilityCruises,
    selectedCruise, setSelectedCruise,
    paxDetails, setPaxDetails,
    agreedTnC, setAgreedTnC,
    bookingRef, setBookingRef,

    // partner identity
    partnerId,
    partnerName,
    tacRate,
    agentId, setAgentId,

    // booking hold + transition
    holdId, setHoldId,
    holdExpiresAt, setHoldExpiresAt,
    holdSeconds,
    holdExpiredNotice, setHoldExpiredNotice,
    fading,
    go,
    exitFlow,
    exitToDashboard,
  };

  return <PartnerBookingContext.Provider value={value}>{children}</PartnerBookingContext.Provider>;
}

export function usePartnerBooking() {
  const ctx = useContext(PartnerBookingContext);
  if (!ctx) throw new Error('usePartnerBooking must be used within a PartnerBookingProvider');
  return ctx;
}
